<script setup lang="ts">
import DirectUploadDropzone from "@/components/DirectUploadDropzone.vue";
import StockImageSearchModal from "@/components/StockImageSearchModal.vue";
import SpecialCharactersModal from "@/components/TipTapEditor/SpecialCharactersModal.vue";
import ToolbarButton from "@/components/TipTapEditor/ToolbarButton.vue";
import ToolbarDropdown from "@/components/TipTapEditor/ToolbarDropdown.vue";
import ToolbarFontSizeDropdown from "@/components/TipTapEditor/ToolbarFontSizeDropdown.vue";
import type { File, FileInput } from "@/services/fobizzToolsApi";
import { useDialog } from "@/utils/dialog";
import { FONT_FAMILIES, FONT_SIZES } from "@/utils/fonts";
import { type Editor, type EditorEvents } from "@tiptap/core";
import { type Level } from "@tiptap/extension-heading";

const { t } = useI18n();
const { openPrompt } = useDialog();
const userStore = useUserStore();
type ToolbarMode = "sm" | "md" | "lg" | "full";
type ToolbarGroup =
  | "header"
  | "format"
  | "undoRedo"
  | "list"
  | "align"
  | "link"
  | "advanced"
  | "table"
  | "embed";
const props = withDefaults(
  defineProps<{
    editor: Editor;
    toolbarMode?: ToolbarMode;
    disabledToolbarGroups?: string[];
    size?: "xs" | "sm" | "md" | "lg";
    navbar?: boolean;
    naked?: boolean;
    groups?: ToolbarGroup[];
    shrine?: boolean;
    parentType?: string;
    parentId?: string;
  }>(),
  {
    toolbarMode: "md",
    disabledToolbarGroups: () => [],
    size: "md",
    navbar: false,
    naked: false,
    groups: () => [
      "header",
      "format",
      "undoRedo",
      "list",
      "align",
      "link",
      "advanced",
      "table",
      // only used by website tool, default is without embed
      // "embed",
    ],
    shrine: false,
    parentType: undefined,
    parentId: undefined,
  }
);

const attachmentMarkedForDeletion = ref<Set<string>>(new Set());

const handleEditorUpdate = ({ transaction }: EditorEvents["update"]) => {
  if (props.shrine) {
    const blobSids = new Set<string>();
    transaction.doc.forEach((node) => {
      if (node.attrs.blobSid) {
        blobSids.add(node.attrs.blobSid);
        attachmentMarkedForDeletion.value.delete(node.attrs.blobSid);
      }
    });
    transaction.before.forEach((node) => {
      if (node.attrs.blobSid && !blobSids.has(node.attrs.blobSid)) {
        attachmentMarkedForDeletion.value.add(node.attrs.blobSid);
      }
    });
  }
};

const handleEditorDestroy = () => {
  if (props.shrine) {
    attachmentMarkedForDeletion.value.forEach(async (blobSid) => {
      await imageAttachtmentService.deleteImageAttachment({
        id: blobSid,
      });
      attachmentMarkedForDeletion.value.delete(blobSid);
    });
  }
};

onMounted(() => {
  props.editor.on("update", handleEditorUpdate);
  props.editor.on("destroy", handleEditorDestroy);
});

onUnmounted(() => {
  props.editor.off("update", handleEditorUpdate);
  props.editor.off("destroy", handleEditorDestroy);
});

const showImageSearchEditor = ref(false);
const showAiImageSearch = ref(false);
const { imageAttachments: imageAttachtmentService } = useApiClient();

const handleImageUploadSuccess = async (blob: Partial<File> | FileInput) => {
  if (props.shrine) {
    const response = await imageAttachtmentService.createImageAttachment({
      requestBody: {
        imageAttachment: {
          attachableType: props.parentType,
          attachableId: props.parentId,
          file: blob as FileInput,
        },
      },
    });

    addImageBlob(response.id);
  } else {
    const signedId = (blob as File).signedId;
    if (signedId) {
      addImageBlob(signedId);
    }
  }
};

const handleImageUploadError = (error: unknown) => {
  console.error(error);
};

const handleImageSearchSubmit = async (blob: Partial<File> | FileInput) => {
  showImageSearchEditor.value = false;
  showAiImageSearch.value = false;

  handleImageUploadSuccess(blob);
};

const addImageBlob = (signedId: string) => {
  const src = `/files/${signedId}`;
  props.editor.chain().focus().setImage({ src, blobSid: signedId }).run();
};

const toggleLink = async () => {
  const previousUrl = props.editor.getAttributes("link").href;

  const url = await openPrompt("URL", "url", t("buttons.save"), {
    inputPlaceholder: "Beispiel: https://example.com",
    inputValue: previousUrl,
    cancelButtonText: t("buttons.cancel"),
    inputValidator: (value: string) => {
      try {
        new URL(value);
        return false;
      } catch (error) {
        return t("errors.invalidUrl");
      }
    },
  });

  // cancelled
  if (url === null) {
    return;
  }

  // empty
  if (url === "") {
    props.editor.chain().focus().extendMarkRange("link").unsetLink().run();

    return;
  }

  // update link
  props.editor
    .chain()
    .focus()
    .extendMarkRange("link")
    .setLink({ href: url })
    .run();
};

const toggleIFrame = async () => {
  const previousUrl = props.editor.getAttributes("src").href;

  const url = await openPrompt("URL", "url", t("buttons.save"), {
    inputPlaceholder: "Beispiel: https://example.com",
    inputValue: previousUrl,
    cancelButtonText: t("buttons.cancel"),
    inputValidator: (value: string) => {
      try {
        new URL(value);
        return false;
      } catch (error) {
        return t("errors.invalidUrl");
      }
    },
  });

  // cancelled
  if (!url || url === "") {
    return;
  }

  // https://www.youtube.com/embed/9bZkp7q19f0
  props.editor.chain().focus().setIframe({ src: url }).run();
};

const isSpecialCharactersModalOpen = ref(false);

const insertCharacter = (character: string) => {
  const cursorPosition = props.editor.state.selection.$anchor.pos;
  if (cursorPosition) {
    // insert
    props.editor.commands.insertContentAt(cursorPosition, character);
  } else {
    // append
    props.editor.commands.insertContent(character);
  }
};

type Action = {
  action: () => void;
  isActive: () => boolean;
  icon: string;
  group: ToolbarGroup;
  label?: string;
  value?: string;
  enabled: boolean;
  toolbarMode: ToolbarMode;
  mobileEnabled: boolean;
};

const actions: Action[] = [
  {
    action: () =>
      props.editor.chain().focus().toggleHeading({ level: 1 }).run(),
    isActive: () => props.editor.isActive("heading", { level: 1 }),
    icon: "bi bi-type-h1",
    group: "header",
    enabled: true,
    toolbarMode: "full",
    mobileEnabled: false,
  },
  {
    action: () =>
      props.editor.chain().focus().toggleHeading({ level: 2 }).run(),
    isActive: () => props.editor.isActive("heading", { level: 2 }),
    icon: "bi bi-type-h2",
    group: "header",
    enabled: true,
    toolbarMode: "lg",
    mobileEnabled: false,
  },
  {
    action: () =>
      props.editor.chain().focus().toggleHeading({ level: 3 }).run(),
    isActive: () => props.editor.isActive("heading", { level: 3 }),
    icon: "bi bi-type-h3",
    group: "header",
    enabled: true,
    toolbarMode: "lg",
    mobileEnabled: false,
  },
  {
    action: () => props.editor.chain().focus().setParagraph().run(),
    isActive: () => props.editor.isActive("paragraph"),
    icon: "bi bi-paragraph",
    group: "header",
    label: "wysiwyg.blockTypes.paragraph",
    enabled: true,
    toolbarMode: "lg",
    mobileEnabled: false,
  },
  {
    action: () => props.editor.chain().focus().toggleBold().run(),
    isActive: () => props.editor.isActive("bold"),
    icon: "bi bi-type-bold",
    group: "format",
    enabled: true,
    toolbarMode: "sm",
    mobileEnabled: true,
  },
  {
    action: () => props.editor.chain().focus().toggleItalic().run(),
    isActive: () => props.editor.isActive("italic"),
    icon: "bi bi-type-italic",
    group: "format",
    enabled: true,
    toolbarMode: "sm",
    mobileEnabled: true,
  },
  {
    action: () => props.editor.chain().focus().toggleUnderline().run(),
    isActive: () => props.editor.isActive("underline"),
    icon: "bi bi-type-underline",
    group: "format",
    enabled: true,
    toolbarMode: "sm",
    mobileEnabled: true,
  },
  {
    action: () => props.editor.chain().focus().toggleStrike().run(),
    isActive: () => props.editor.isActive("strike"),
    icon: "bi bi-type-strikethrough",
    group: "format",
    enabled: true,
    toolbarMode: "md",
    mobileEnabled: false,
  },
  {
    action: () => props.editor.chain().focus().toggleCode().run(),
    isActive: () => props.editor.isActive("code"),
    icon: "bi bi-code",
    group: "format",
    enabled: true,
    toolbarMode: "lg",
    mobileEnabled: false,
  },
  {
    action: () => props.editor.chain().focus().toggleHighlight().run(),
    isActive: () => props.editor.isActive("highlight"),
    icon: "bi bi-pen font-sm p-1",
    group: "format",
    enabled: true,
    toolbarMode: "sm",
    mobileEnabled: false,
  },
  {
    action: () =>
      props.editor.chain().focus().addKatexInline({ content: "" }).run(),
    isActive: () => false,
    icon: "bi bi-braces-asterisk",
    group: "format",
    enabled: true,
    toolbarMode: "sm",
    mobileEnabled: false,
  },
  {
    action: () => props.editor.chain().focus().undo().run(),
    isActive: () => true,
    icon: "bi bi-arrow-counterclockwise",
    group: "undoRedo",
    enabled: true,
    toolbarMode: "sm",
    mobileEnabled: true,
  },
  {
    action: () => props.editor.chain().focus().redo().run(),
    isActive: () => true,
    icon: "bi bi-arrow-clockwise",
    group: "undoRedo",
    enabled: true,
    toolbarMode: "sm",
    mobileEnabled: false,
  },
  {
    action: () => props.editor.chain().focus().toggleBulletList().run(),
    isActive: () => props.editor.isActive("bulletList"),
    icon: "bi bi-list-ul",
    group: "list",
    enabled: true,
    toolbarMode: "md",
    mobileEnabled: false,
  },
  {
    action: () => props.editor.chain().focus().toggleOrderedList().run(),
    isActive: () => props.editor.isActive("orderedList"),
    icon: "bi bi-list-ol",
    group: "list",
    enabled: true,
    toolbarMode: "md",
    mobileEnabled: false,
  },
  {
    action: () => props.editor.chain().focus().setTextAlign("left").run(),
    isActive: () => props.editor.isActive({ textAlign: "left" }),
    icon: "bi bi-text-left",
    group: "align",
    value: "left",
    enabled: true,
    toolbarMode: "sm",
    mobileEnabled: false,
  },
  {
    action: () => props.editor.chain().focus().setTextAlign("center").run(),
    isActive: () => props.editor.isActive({ textAlign: "center" }),
    icon: "bi bi-text-center",
    group: "align",
    value: "center",
    enabled: true,
    toolbarMode: "sm",
    mobileEnabled: false,
  },
  {
    action: () => props.editor.chain().focus().setTextAlign("right").run(),
    isActive: () => props.editor.isActive({ textAlign: "right" }),
    icon: "bi bi-text-right",
    group: "align",
    value: "right",
    enabled: true,
    toolbarMode: "sm",
    mobileEnabled: false,
  },
  {
    action: () => toggleLink(),
    isActive: () => props.editor.isActive("link"),
    icon: "bi bi-link-45deg",
    group: "link",
    enabled: true,
    toolbarMode: "sm",
    mobileEnabled: true,
  },
  {
    action: () => props.editor.chain().focus().toggleBlockquote().run(),
    isActive: () => props.editor.isActive("blockquote"),
    icon: "bi bi-quote",
    label: "blockquote",
    group: "advanced",
    enabled: true,
    toolbarMode: "lg",
    mobileEnabled: false,
  },
  {
    action: () => props.editor.chain().focus().toggleCodeBlock().run(),
    isActive: () => props.editor.isActive("codeBlock"),
    icon: "bi bi-code",
    label: "codeBlock",
    group: "advanced",
    enabled: true,
    toolbarMode: "lg",
    mobileEnabled: false,
  },
  {
    action: () => (isSpecialCharactersModalOpen.value = true),
    isActive: () => true,
    icon: "bi bi-hash",
    label: "specialCharacters",
    group: "advanced",
    enabled: true,
    toolbarMode: "lg",
    mobileEnabled: false,
  },
  {
    action: () => props.editor.chain().focus().setHorizontalRule().run(),
    isActive: () => props.editor.isActive("horizontalRule"),
    icon: "bi bi-hr",
    label: "horizontalRule",
    group: "advanced",
    enabled: true,
    toolbarMode: "lg",
    mobileEnabled: false,
  },
  {
    action: () =>
      props.editor
        .chain()
        .focus()
        .insertTable({ rows: 3, cols: 3, withHeaderRow: false })
        .run(),
    isActive: () => props.editor.isActive("table"),
    icon: "bi bi-table",
    label: "table",
    group: "table",
    enabled: true,
    toolbarMode: "lg",
    mobileEnabled: false,
  },
  {
    action: () => toggleIFrame(),
    isActive: () => props.editor.isActive("iframe"),
    icon: "bi bi-code-square",
    label: "iframe",
    group: "embed",
    enabled: true,
    toolbarMode: "full",
    mobileEnabled: false,
  },
  {
    action: () =>
      props.editor
        .chain()
        .focus()
        .addKatex({ content: "x^2 + y^2 = z^2" })
        .run(),
    isActive: () => props.editor.isActive("formula"),
    icon: "bi bi-braces-asterisk",
    label: "katex",
    group: "advanced",
    enabled: true,
    toolbarMode: "lg",
    mobileEnabled: false,
  },
];

const matchToolbarMode = (toolbarMode: ToolbarMode): boolean => {
  if (toolbarMode === "full") {
    return props.toolbarMode === "full";
  } else if (toolbarMode === "lg") {
    return props.toolbarMode === "lg" || props.toolbarMode === "full";
  } else if (toolbarMode === "md") {
    return (
      props.toolbarMode === "md" ||
      props.toolbarMode === "lg" ||
      props.toolbarMode === "full"
    );
  } else if (toolbarMode === "sm") {
    return (
      props.toolbarMode === "sm" ||
      props.toolbarMode === "md" ||
      props.toolbarMode === "lg" ||
      props.toolbarMode === "full"
    );
  } else {
    throw new Error(`Invalid toolbar mode ${toolbarMode}`);
  }
};

const inDisabledToolbarGroups = (group: string): boolean => {
  if (props.disabledToolbarGroups) {
    return props.disabledToolbarGroups.includes(group);
  } else {
    return true;
  }
};

const enabledActions = computed(() =>
  actions
    .filter((action) => action.enabled)
    .filter((action) => matchToolbarMode(action.toolbarMode))
    .filter((action) => !inDisabledToolbarGroups(action.group))
);

const mobileEnabledActions = computed(() =>
  actions
    .filter((action) => action.mobileEnabled)
    .filter((action) => matchToolbarMode(action.toolbarMode))
    .filter((action) => !inDisabledToolbarGroups(action.group))
);

const headerButtons = computed(() => {
  return enabledActions.value.filter((action) => action.group === "header");
});

const mobileHeaderButtons = computed(() => {
  return mobileEnabledActions.value.filter(
    (action) => action.group === "header"
  );
});

const formatButtons = computed(() => {
  return enabledActions.value.filter((action) => action.group === "format");
});

const mobileFormatButtons = computed(() => {
  return mobileEnabledActions.value.filter(
    (action) => action.group === "format"
  );
});

const undoRedoButtons = computed(() => {
  return enabledActions.value.filter((action) => action.group === "undoRedo");
});

const mobileUndoRedoButtons = computed(() => {
  return mobileEnabledActions.value.filter(
    (action) => action.group === "undoRedo"
  );
});

const mobileListButtons = computed(() => {
  return mobileEnabledActions.value.filter((action) => action.group === "list");
});

const listButtons = computed(() => {
  return enabledActions.value.filter((action) => action.group === "list");
});

const alignButtons = computed(() => {
  return enabledActions.value.filter((action) => action.group === "align");
});

const linkButtons = computed(() => {
  return enabledActions.value.filter((action) => action.group === "link");
});

const advancedButtons = computed(() => {
  return enabledActions.value.filter((action) => action.group === "advanced");
});

const tableButtons = computed(() => {
  return enabledActions.value.filter((action) => action.group === "table");
});

const embedButtons = computed(() => {
  return (
    (props.groups.includes("embed") &&
      enabledActions.value.filter((action) => action.group === "embed")) ||
    []
  );
});

const changeFontSize = (value: number) => {
  if (value === 16) {
    props.editor.chain().focus().unsetFontSize().run();
    return;
  }
  props.editor.chain().focus().setFontSize(`${value}px`).run();
};

const incrementFontSize = () => {
  const current = currentFontSize.value;
  if (current === null) {
    return;
  }
  const index = FONT_SIZES.findIndex((fontSize) => {
    return fontSize.value === current;
  });
  if (index === -1) {
    return;
  }
  const next = FONT_SIZES[index + 1];
  if (next) {
    changeFontSize(next.value);
  }
};

const decrementFontSize = () => {
  const current = currentFontSize.value;
  if (current === null) {
    return;
  }
  const index = FONT_SIZES.findIndex((fontSize) => {
    return fontSize.value === current;
  });
  if (index === -1) {
    return;
  }
  const prev = FONT_SIZES[index - 1];
  if (prev) {
    changeFontSize(prev.value);
  }
};

// font-size uses px, but FONT_SIZES has no unit
const currentFontSize = computed(() => {
  const size = props.editor.getAttributes("textStyle").fontSize;
  const match = FONT_SIZES.find((fontSize) => {
    return size === `${fontSize.value}px`;
  });

  if (match) {
    return match.label;
  }

  if (props.editor.isActive("heading", { level: 1 })) {
    return 48;
  } else if (props.editor.isActive("heading", { level: 2 })) {
    return 36;
  } else if (props.editor.isActive("heading", { level: 3 })) {
    return 30;
  } else if (props.editor.isActive("paragraph")) {
    return 16;
  }

  return null;
});

const changeFontFamily = (fontFamily: string) => {
  props.editor.chain().focus().setFontFamily(fontFamily).run();
};

// font-family has no quoted strings, but FONT_FAMILIES uses quoted strings
// we need to replace all the quotes for comparison
const currentFontFamilyLabel = computed(() => {
  const currentFontFamily = props.editor.getAttributes("textStyle").fontFamily;
  const match = FONT_FAMILIES.find((fontFamily) => {
    return currentFontFamily === fontFamily.value.replaceAll('"', "");
  });
  if (match) {
    return match.label;
  }
  return FONT_FAMILIES[0].label;
});

const BLOCK_TYPES = [
  {
    value: "p",
    label: t("wysiwyg.blockTypes.paragraph"),
  },
  {
    value: "h1",
    label: t("wysiwyg.blockTypes.one"),
  },
  {
    value: "h2",
    label: t("wysiwyg.blockTypes.two"),
  },
  {
    value: "h3",
    label: t("wysiwyg.blockTypes.three"),
  },
  {
    value: "h4",
    label: t("wysiwyg.blockTypes.four"),
  },
  {
    value: "h5",
    label: t("wysiwyg.blockTypes.five"),
  },
  {
    value: "h6",
    label: t("wysiwyg.blockTypes.six"),
  },
];

const changeBlockType = (blockType: string) => {
  if (blockType === "p") {
    props.editor.chain().focus().setParagraph().run();
  } else if (blockType.startsWith("h")) {
    const level = parseInt(blockType[1]) as Level;
    props.editor.chain().focus().toggleHeading({ level }).run();
  }
};

const currentBlockTypeLabel = computed(() => {
  if (props.editor.isActive("heading", { level: 1 })) {
    return t("wysiwyg.blockTypes.one");
  } else if (props.editor.isActive("heading", { level: 2 })) {
    return t("wysiwyg.blockTypes.two");
  } else if (props.editor.isActive("heading", { level: 3 })) {
    return t("wysiwyg.blockTypes.three");
  } else if (props.editor.isActive("heading", { level: 4 })) {
    return t("wysiwyg.blockTypes.four");
  } else if (props.editor.isActive("heading", { level: 5 })) {
    return t("wysiwyg.blockTypes.five");
  } else if (props.editor.isActive("heading", { level: 6 })) {
    return t("wysiwyg.blockTypes.six");
  } else if (props.editor.isActive("paragraph")) {
    return t("wysiwyg.blockTypes.paragraph");
  } else {
    return t("wysiwyg.blockTypes.paragraph");
  }
});

const changeTextAlign = (textAlign: string) => {
  props.editor.chain().focus().setTextAlign(textAlign).run();
};

const TEXT_ALIGNS = ["left", "center", "right"];

const currentTextAlign = computed(() => {
  const match = TEXT_ALIGNS.find((textAlign) => {
    return props.editor.isActive({ textAlign });
  });

  if (match) {
    return alignButtons.value.find((button) => {
      return button.value === match;
    });
  }

  return alignButtons.value[0];
});
</script>
<template>
  <div class="mobile-toolbar">
    <div
      v-if="editor"
      class="wysiwyg-toolbar-container"
      :class="{
        'wysiwyg-toolbar-container--navbar main-margin': navbar,
      }"
    >
      <TipTapEditorToolbar :naked="naked" :size="size" :container="navbar">
        <ToolbarButton
          v-for="button in mobileUndoRedoButtons"
          :key="button.icon"
          :active="false"
          fixed
          @click.prevent="button.action"
        >
          <i :class="button.icon"></i>
        </ToolbarButton>

        <TipTapEditorToolbarSeparator />

        <ToolbarFontSizeDropdown
          v-if="matchToolbarMode('lg')"
          id="wysiwyg-font-size"
          class="ms-2"
          border
          @increment="incrementFontSize"
          @decrement="decrementFontSize"
        >
          <template #button>
            <span v-if="currentFontSize" style="font-size: 0.9rem">
              {{ currentFontSize }}
            </span>
            <span v-else class="text-muted" style="font-size: 1rem">16</span>
          </template>
          <BaseDropdownItem
            v-for="fontSize in FONT_SIZES"
            :key="fontSize.value"
            :value="fontSize.value"
            @click="changeFontSize(fontSize.value)"
          >
            {{ fontSize.label }}
          </BaseDropdownItem>
        </ToolbarFontSizeDropdown>

        <TipTapEditorToolbarSeparator />

        <template v-if="mobileHeaderButtons.length > 0 && toolbarMode === 'md'">
          <ToolbarButton
            v-for="button in mobileHeaderButtons"
            :key="button.icon"
            :active="button.isActive()"
            fixed
            @click.prevent="button.action"
          >
            <i :class="button.icon"></i>
          </ToolbarButton>
        </template>

        <TipTapEditorToolbarSeparator />

        <ToolbarButton
          v-for="button in mobileFormatButtons"
          :key="button.icon"
          :active="button.isActive()"
          fixed
          @click.prevent="button.action"
        >
          <i :class="button.icon"></i>
        </ToolbarButton>

        <TipTapEditorToolbarSeparator />

        <ToolbarDropdown id="wysiwyg-align">
          <template #button>
            <span v-if="currentTextAlign">
              <i :class="currentTextAlign.icon"></i>
            </span>
          </template>
          <BaseDropdownItem
            v-for="alignButton in alignButtons"
            :key="alignButton.value"
            @click="changeTextAlign(alignButton.value || 'left')"
          >
            <i :class="alignButton.icon"></i>
          </BaseDropdownItem>
        </ToolbarDropdown>

        <TipTapEditorToolbarSeparator />

        <ToolbarButton
          v-for="button in mobileListButtons"
          :key="button.icon"
          :active="button.isActive()"
          fixed
          @click.prevent="button.action"
        >
          <i :class="button.icon"></i>
        </ToolbarButton>

        <TipTapEditorToolbarSeparator />

        <ToolbarButton
          v-for="button in linkButtons"
          :key="button.icon"
          :active="button.isActive()"
          @click.prevent="button.action"
        >
          <i :class="button.icon"></i>
        </ToolbarButton>

        <ToolbarDropdown
          v-if="matchToolbarMode('md')"
          id="wysiwyg-more"
          fixed-min-width
          align="right"
          :button-attrs="{ 'data-testid': 'dropdown-more' }"
        >
          <template #button>
            <span>
              <i class="bi bi-plus-lg"></i>
            </span>
          </template>

          <template v-if="matchToolbarMode('md')">
            <DirectUploadDropzone
              :allowed-types="['image/jpeg', 'image/png', 'image/gif']"
              :max-file-size="5"
              attachment-name="Block#images"
              @upload:success="handleImageUploadSuccess"
              @upload:error="handleImageUploadError"
            >
              <template #default="{ openFileBrowser }">
                <BaseDropdownItem
                  data-testid="link-open-file-browser"
                  icon-name="image"
                  @click.prevent="openFileBrowser"
                >
                  {{ t("links.uploadImage") }}
                </BaseDropdownItem>
                <BaseDropdownItem
                  icon-name="search"
                  @click.prevent="showImageSearchEditor = true"
                >
                  {{ t("links.imageSearch") }}
                </BaseDropdownItem>
                <BaseDropdownItem
                  v-if="userStore.isFeatureEnabled('ai_image_search')"
                  @click.prevent="showAiImageSearch = true"
                >
                  <i class="bi bi-robot"></i>
                  {{ t("links.aiImageSearch") }}
                </BaseDropdownItem>
              </template>
            </DirectUploadDropzone>
          </template>
        </ToolbarDropdown>
      </TipTapEditorToolbar>
    </div>
  </div>
  <div class="desktop-toolbar">
    <div
      v-if="editor"
      class="wysiwyg-toolbar-container"
      :class="{
        'wysiwyg-toolbar-container--navbar main-margin': navbar,
      }"
    >
      <TipTapEditorToolbar :naked="naked" :size="size" :container="navbar">
        <ToolbarButton
          v-for="button in undoRedoButtons"
          :key="button.icon"
          :active="false"
          fixed
          @click.prevent="button.action"
        >
          <i :class="button.icon"></i>
        </ToolbarButton>

        <TipTapEditorToolbarSeparator />

        <template v-if="headerButtons.length > 0 && toolbarMode === 'md'">
          <ToolbarButton
            v-for="button in headerButtons"
            :key="button.icon"
            :active="button.isActive()"
            fixed
            @click.prevent="button.action"
          >
            <i :class="button.icon"></i>
          </ToolbarButton>
        </template>

        <ToolbarDropdown
          v-if="matchToolbarMode('full')"
          id="wysiwyg-block-type"
          style="width: 120px"
          border
        >
          <template #button>
            <span
              style="
                font-size: 0.9rem;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              "
              >{{ currentBlockTypeLabel }}</span
            >
          </template>
          <BaseDropdownItem
            v-for="blockType in BLOCK_TYPES"
            :key="blockType.value"
            :value="blockType.value"
            @click="changeBlockType(blockType.value)"
          >
            {{ blockType.label }}
          </BaseDropdownItem>
        </ToolbarDropdown>

        <ToolbarDropdown
          v-if="matchToolbarMode('lg')"
          id="wysiwyg-font-family"
          class="ms-2"
          style="width: 100px"
          border
        >
          <template #button>
            <span
              style="
                font-size: 0.9rem;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              "
            >
              {{ currentFontFamilyLabel }}
            </span>
          </template>
          <BaseDropdownItem
            v-for="fontFamily in FONT_FAMILIES"
            :key="fontFamily.value"
            :value="fontFamily.value"
            @click="changeFontFamily(fontFamily.value)"
          >
            <span :style="{ fontFamily: fontFamily.value }">
              {{ fontFamily.label }}
            </span>
          </BaseDropdownItem>
        </ToolbarDropdown>

        <ToolbarFontSizeDropdown
          v-if="matchToolbarMode('lg')"
          id="wysiwyg-font-size"
          class="ms-2"
          border
          @increment="incrementFontSize"
          @decrement="decrementFontSize"
        >
          <template #button>
            <span v-if="currentFontSize" style="font-size: 0.9rem">
              {{ currentFontSize }}
            </span>
            <span v-else class="text-muted" style="font-size: 1rem">16</span>
          </template>
          <BaseDropdownItem
            v-for="fontSize in FONT_SIZES"
            :key="fontSize.value"
            :value="fontSize.value"
            @click="changeFontSize(fontSize.value)"
          >
            {{ fontSize.label }}
          </BaseDropdownItem>
        </ToolbarFontSizeDropdown>

        <TipTapEditorToolbarSeparator />

        <ToolbarButton
          v-for="button in formatButtons"
          :key="button.icon"
          :active="button.isActive()"
          fixed
          @click.prevent="button.action"
        >
          <i :class="button.icon"></i>
        </ToolbarButton>

        <TipTapEditorToolbarSeparator />

        <ToolbarDropdown id="wysiwyg-align">
          <template #button>
            <span v-if="currentTextAlign">
              <i :class="currentTextAlign.icon"></i>
            </span>
          </template>
          <BaseDropdownItem
            v-for="alignButton in alignButtons"
            :key="alignButton.value"
            @click="changeTextAlign(alignButton.value || 'left')"
          >
            <i :class="alignButton.icon"></i>
          </BaseDropdownItem>
        </ToolbarDropdown>

        <TipTapEditorToolbarSeparator />

        <ToolbarButton
          v-for="button in listButtons"
          :key="button.icon"
          :active="button.isActive()"
          fixed
          @click.prevent="button.action"
        >
          <i :class="button.icon"></i>
        </ToolbarButton>

        <TipTapEditorToolbarSeparator />

        <ToolbarButton
          v-for="button in linkButtons"
          :key="button.icon"
          :active="button.isActive()"
          @click.prevent="button.action"
        >
          <i :class="button.icon"></i>
        </ToolbarButton>

        <ToolbarDropdown
          v-if="matchToolbarMode('md')"
          id="wysiwyg-more"
          fixed-min-width
          align="right"
          :button-attrs="{ 'data-testid': 'dropdown-more' }"
        >
          <template #button>
            <span>
              <i class="bi bi-plus-lg"></i>
            </span>
          </template>

          <template v-if="matchToolbarMode('md')">
            <DirectUploadDropzone
              :allowed-types="['image/jpeg', 'image/png', 'image/gif']"
              :max-file-size="5"
              attachment-name="Block#images"
              :shrine="shrine"
              @upload:success="handleImageUploadSuccess"
              @upload:error="handleImageUploadError"
            >
              <template #default="{ openFileBrowser }">
                <BaseDropdownItem
                  data-testid="link-open-file-browser"
                  @click.prevent="openFileBrowser"
                >
                  <i class="bi bi-image"></i>
                  {{ t("links.uploadImage") }}
                </BaseDropdownItem>
                <BaseDropdownItem @click.prevent="showImageSearchEditor = true">
                  <i class="bi bi-search"></i>
                  {{ t("links.imageSearch") }}
                </BaseDropdownItem>
                <BaseDropdownItem
                  v-if="userStore.isFeatureEnabled('ai_image_search')"
                  @click.prevent="showAiImageSearch = true"
                >
                  <i class="bi bi-robot"></i>
                  {{ t("links.aiImageSearch") }}
                </BaseDropdownItem>
              </template>
            </DirectUploadDropzone>
            <BaseDropdownDivider
              v-if="
                tableButtons.length > 0 ||
                matchToolbarMode('lg') ||
                matchToolbarMode('full')
              "
            />
            <BaseDropdownItem
              v-for="button in tableButtons"
              :key="button.icon"
              @click.prevent="button.action"
            >
              <i :class="button.icon"></i>
              {{ t(`wysiwyg.actions.${button.label}`) }}
            </BaseDropdownItem>
          </template>

          <template v-if="matchToolbarMode('lg')">
            <BaseDropdownItem
              v-for="button in advancedButtons"
              :key="button.icon"
              @click.prevent="button.action"
            >
              <i :class="button.icon"></i>
              {{ t(`wysiwyg.actions.${button.label}`) }}
            </BaseDropdownItem>
          </template>
          <template v-if="matchToolbarMode('full')">
            <BaseDropdownItem
              v-for="button in embedButtons"
              :key="button.icon"
              @click.prevent="button.action"
            >
              <i :class="button.icon"></i>
              {{ t(`wysiwyg.actions.${button.label}`) }}
            </BaseDropdownItem>
          </template>
        </ToolbarDropdown>

        <div class="d-flex align-items-center">
          <slot name="insert-actions"></slot>
        </div>

        <div class="d-flex align-items-center ms-auto">
          <slot name="hint"></slot>
        </div>
      </TipTapEditorToolbar>
    </div>
  </div>

  <StockImageSearchModal
    :shrine="shrine"
    :open="showImageSearchEditor"
    @close="showImageSearchEditor = false"
    @submit="handleImageSearchSubmit"
  />

  <AiImageSearchModal
    :open="showAiImageSearch"
    @close="showAiImageSearch = false"
    @submit="handleImageSearchSubmit"
  />

  <SpecialCharactersModal
    :open="isSpecialCharactersModalOpen"
    @hide="isSpecialCharactersModalOpen = false"
    @insert="insertCharacter"
  />
</template>

<style lang="scss" scoped>
.mobile-toolbar {
  visibility: hidden;
  height: 0;
  width: 0;

  .wysiwyg-toolbar {
    flex-wrap: nowrap;
    justify-content: space-between;
  }

  @include media-breakpoint-down(sm) {
    visibility: visible;
    height: 100%;
    width: 100%;
    align-content: center;
    display: flex;
  }
}

.desktop-toolbar {
  display: flex;
  visibility: visible;
  @include media-breakpoint-down(sm) {
    visibility: hidden;
    height: 0;
    width: 0;
  }
}

.wysiwyg-toolbar-container {
  position: relative;
  width: 100%;

  &--navbar {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: nowrap;
    width: 100%;
    padding: 0 $spacer-3;
  }

  &--fixed {
    position: fixed;
    top: $header-height-mobile;
    left: 0;
    right: 0;
    z-index: 1;
    max-width: 1024px;

    // space filled between fixed header and wysiwyg-toolbar
    padding-top: 2rem;
    background-color: $body-bg;
    background-image: radial-gradient(#eee 0.5px, transparent 0.5px);
    background-size: 5px 5px;
  }
}
</style>
